import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/BootstrapTextField";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { addItem, fetchItemById, updateItem } from "slices/item/thunk";
import * as Yup from "yup";
import { autocompleteDropdownStyles } from "Constants/Style";
import PageContainer from "components/PageContainer/PageContainer";

const colorFields = ["Black", "White"];

// Common input style for Form.Control to match Autocomplete styling
const commonInputStyle = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "13px",
};

const ItemForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [unitOption, setUnitOption] = useState<[]>([]);
  const [hsnOption, setHSNOption] = useState<[]>([]);
  const [categoryOption, setCategoryOption] = useState<[]>([]);
  const [subCategoryOption, setSubCategoryOption] = useState<[]>([]);
  const dispatch = useDispatch<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [initialData, setInitialData] = useState({
    item_id: "",
    item_name: "",
    alternate_name: "",
    item_code: "",
    item_gst: "",
    unit_id: "",
    hsn_code_id: "",
    category_id: "",
    sub_category_id: "",
    selling_price: "",
    color: "",
    description: "",
    remark: "",
  });

  useEffect(() => {
    // If editing auto fill details
    if (id) {
      setIsLoading(true);
      dispatch(fetchItemById(id))
        .unwrap()
        .then((data: any) => {
          data = data.data.data;
          setInitialData({
            item_id: data?.item_id,
            item_name: data?.item_name,
            alternate_name: data?.alternate_name || "",
            item_code: data?.item_code,
            unit_id: data?.unit?.unit_id || "",
            item_gst: data?.hsn_code?.gst_percent?.gst_percent,
            hsn_code_id: data?.hsn_code?.hsn_code_id || "",
            category_id: data?.category?.category_id || "",
            sub_category_id: data?.sub_category?.sub_category_id || "",
            color: data?.color || "",
            selling_price: data?.selling_price || "",
            description: data?.description,
            remark: data?.remark || "",
          });

          if (data.category) {
            return Axios.get(
              `/master/sub-category/dropdown/category/${data.category?.category_id}`
            )
              .then((res: any) => {
                const options = res?.data?.data || [];
                setSubCategoryOption(options);
              })
              .catch(() => {
                toast.error("Failed to fetch subcategories");
                setSubCategoryOption([]);
              });
          }
        })
        .catch((err: any) => {
          toast.error("Failed to fetch Item");
          // navigate("/packages");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    // Get new item code
    else {
      setIsLoading(true);
      Axios.get("/master/item/next-code")
        .then((res) => {
          let data = res.data.data;
          setInitialData({
            ...initialData,
            item_code: data?.next_item_code,
          });
        })
        .catch((err) => {
          toast.error("Failed to fetch Next Item Code");
        })
        .finally(() => setIsLoading(false));
    }
  }, [id]);

  //*// Get dropdowns
  useEffect(() => {
    Axios.get("/master/unit/dropdown/")
      .then((success: any) => {
        // console.log("success?.data?.data", success?.data?.data);
        setUnitOption(success?.data?.data || []);
      })
      .catch(() => {
        toast.error("Failed to fetch Unit Dropdown");
      });
    Axios.get("/master/hsn/dropdown/")
      .then((success: any) => {
        setHSNOption(success?.data?.data || []);
      })
      .catch(() => {
        toast.error("Failed to fetch HSN Dropdown");
      });
    Axios.get("/master/category/dropdown/")
      .then((success: any) => {
        setCategoryOption(success?.data?.data || []);
      })
      .catch(() => {
        toast.error("Failed to fetch Category Dropdown");
      });
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      item_name: initialData.item_name || "",
      alternate_name: initialData.alternate_name || "",
      item_code: initialData.item_code || "",
      item_gst: initialData.item_gst || "",
      unit_id: initialData.unit_id || "",
      hsn_code_id: initialData.hsn_code_id || "",
      category_id: initialData.category_id || "",
      sub_category_id: initialData.sub_category_id || "",
      selling_price: initialData.selling_price || "",
      color: initialData.color || "",
      description: initialData.description || "",
      remark: initialData.remark || "",
    },
    validationSchema: Yup.object({
      item_name: Yup.string()
        .required("Please enter an Item Name")
        .matches(
          /^[A-Za-z0-9\s-]+$/,
          "Item name must contain only letters, numbers, spaces, and hyphens"
        ),

      item_code: Yup.string().required("Please enter Item Code"),
      alternate_name: Yup.string().required("Please enter Alternate Name"),
      item_gst: Yup.string().required("Please enter a GST"),
      unit_id: Yup.string().required("Please select a Unit"),
      hsn_code_id: Yup.string().required("Please select an HSN code"),
      category_id: Yup.string().required("Please select a category"),
      sub_category_id: Yup.string().required("Please select a Sub Category"),
      selling_price: Yup.number()
        .typeError("Selling Price must be a number")
        .required("Please enter Selling Price")
        .moreThan(0, "Selling Price must be greater than 0"),
      color: Yup.string().required("Please Select a Color"),
    }),
    onSubmit: async (values) => {
      // Convert selling_price to number
      const sellingPrice = values.selling_price
        ? typeof values.selling_price === "string"
          ? parseFloat(values.selling_price)
          : Number(values.selling_price)
        : null;

      let payload: any;

      if (isEdit) {
        // Update payload: use unit, category, sub_category, hsn_code (not _id)
        payload = {
          item_name: values.item_name,
          alternate_name: values.alternate_name,
          item_code: values.item_code,
          unit: values.unit_id,
          category: values?.category_id,
          sub_category: values.sub_category_id,
          selling_price: sellingPrice,
          color: values.color,
          hsn_code: values.hsn_code_id,
          remark: values.remark !== undefined ? values.remark : null,
        };
      } else {
        // Create payload: use unit_id, category_id, sub_category_id, hsn_code_id
        payload = {
          item_name: values.item_name,
          alternate_name: values.alternate_name,
          item_code: values.item_code,
          unit_id: values.unit_id,
          category_id: values?.category_id,
          sub_category_id: values.sub_category_id,
          selling_price: sellingPrice,
          color: values.color,
          hsn_code_id: values.hsn_code_id,
          remark: values.remark || null,
        };
      }

      try {
        let result;

        if (isEdit) {
          result = await dispatch(
            updateItem({ id: initialData?.item_id, payload: payload })
          ).unwrap();
        } else {
          result = await dispatch(addItem(payload)).unwrap();
        }

        toast.success(
          result?.message ||
            (isEdit ? "Item updated successfully" : "Item added successfully")
        );
        // Clear state when creating new item, preserve when editing
        navigate("/item", { state: { clearState: !isEdit } });
      } catch (error: any) {
        toast.error(
          error?.error || error?.message || "Something went wrong while saving the Item"
        );
      }
    },
  });

  return (
    <PageContainer>
      {/* Heading and breadcrumb */}
      <FormHeader
        title={isEdit ? "Edit Item" : "Add Item"}
        firstLabel="Item"
        secondLabel={isEdit ? "Edit Item" : "Add Item"}
        handleArrowClick={() => navigate("/item")}
      />

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-3">
                <Form.Label>
                  Item Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="item_name"
                  placeholder="Enter Item Name"
                  value={formik.values.item_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.item_name && formik.touched.item_name
                  }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.item_name}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* Alternate Name  */}
            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-3">
                <Form.Label>
                  Alternate Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="alternate_name"
                  placeholder="Enter Alternate Name"
                  value={formik.values.alternate_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.alternate_name &&
                    formik.touched.alternate_name
                  }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.alternate_name}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-3">
                <Form.Label>
                  Item Code <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="item_code"
                  placeholder="Enter Item Code"
                  value={formik.values.item_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.item_code && formik.touched.item_code
                  }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.item_code}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* Category */}
            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-4">
                <Form.Label>
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={categoryOption}
                  getOptionLabel={(option: any) =>
                    typeof option === "string" ? option : option.category_name
                  }
                  value={
                    categoryOption?.find(
                      (unit: any) =>
                        unit.category_id === formik.values.category_id
                    ) || null
                  }
                  onChange={(event, val) => {
                    // console.log(`val: ${JSON.stringify(val)}`);
                    const category_id = val?.category_id || "";
                    // console.log(`categoryId: ${JSON.stringify(categoryId)}`);
                    formik.setFieldValue("category_id", val?.category_id || "");
                    formik.setFieldValue("sub_category_id", "");

                    // Step 3: Fetch related subcategories
                    if (category_id) {
                      Axios.get(
                        `/master/sub-category/dropdown/category/${category_id}`
                      )
                        .then((res: any) => {
                          setSubCategoryOption(res?.data?.data || []);
                        })
                        .catch(() => {
                          toast.error("Failed to fetch subcategories");
                          setSubCategoryOption([]);
                        });
                    } else {
                      setSubCategoryOption([]);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("category_id", true)}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        typeof opt === "string" ? opt : opt.category_name
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      name="category_id"
                      placeholder="Select Category"
                      error={
                        formik.touched.category_id &&
                        Boolean(formik.errors.category_id)
                      }
                      helperText={
                        formik.touched.category_id && formik.errors.category_id
                      }
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.category_id}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* Sub Category */}
            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-4">
                <Form.Label>
                  Sub Category <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={subCategoryOption}
                  getOptionLabel={(option: any) =>
                    typeof option === "string"
                      ? option
                      : option.sub_category_name
                  }
                  value={
                    subCategoryOption?.find(
                      (sub: any) =>
                        sub.sub_category_id === formik.values.sub_category_id
                    ) || null
                  }
                  onChange={(event, val) => {
                    formik.setFieldValue(
                      "sub_category_id",
                      val?.sub_category_id || ""
                    );
                  }}
                  onBlur={() => formik.setFieldTouched("sub_category_id", true)}
                  noOptionsText={
                    !formik.values.category_id
                      ? "Select Category first"
                      : "No options"
                  }
                  disabled={!formik.values.category_id}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        typeof opt === "string" ? opt : opt.sub_category_name
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      name="sub_category_id"
                      placeholder="Select Sub Category"
                      error={
                        formik.touched.sub_category_id &&
                        Boolean(formik.errors.sub_category_id)
                      }
                      helperText={
                        formik.touched.sub_category_id &&
                        formik.errors.sub_category_id
                      }
                      fullWidth
                      size="small"
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.sub_category_id}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* HSN code */}
            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-4">
                <Form.Label>
                  HSN Code <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={hsnOption}
                  getOptionLabel={(option: any) =>
                    typeof option === "string" ? option : option.hsn_code
                  }
                  value={
                    hsnOption?.find(
                      (hsn: any) =>
                        hsn.hsn_code_id === formik.values.hsn_code_id
                    ) || null
                  }
                  onChange={(event, val) => {
                    // console.log(`val: ${JSON.stringify(val)}`);
                    // const hsn_code_id = val?.hsn_code_id || "";
                    formik.setFieldValue("hsn_code_id", val?.hsn_code_id || "");
                    formik.setFieldValue(
                      "item_gst",
                      val?.gst_percent?.gst_percent || ""
                    );
                    // if (hsn_code_id) {
                    //   Axios.get(`/master/item/hsn/dropdown/${hsn_code_id}`)
                    //     .then((res: any) => {
                    //       // console.log(`res of hsn : ${JSON.stringify(res)}`);
                    //       const gstFromAPI = res?.data?.data?.gst_percentage || "";
                    //       // console.log(
                    //       //   `gstFromAPI : ${JSON.stringify(gstFromAPI)}`
                    //       // );
                    //       formik.setFieldValue("item_gst", gstFromAPI);
                    //     })
                    //     .catch(() => {
                    //       toast.error("Failed to fetch GST by HSN Code");
                    //       formik.setFieldValue("item_gst", "");
                    //     });
                    // } else {
                    //   formik.setFieldValue("item_gst", "");
                    // }
                  }}
                  onBlur={() => formik.setFieldTouched("hsn_code_id", true)}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        typeof opt === "string" ? opt : opt.hsn_code
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      name="hsn_code_id"
                      placeholder="Select HSN Code"
                      error={
                        formik.touched.hsn_code_id &&
                        Boolean(formik.errors.hsn_code_id)
                      }
                      helperText={
                        formik.touched.hsn_code_id && formik.errors.hsn_code_id
                      }
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.hsn_code_id}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* unit */}
            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-4">
                <Form.Label>
                  Unit <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={unitOption}
                  getOptionLabel={(option: any) =>
                    typeof option === "string" ? option : option.unit_name
                  }
                  value={
                    unitOption?.find(
                      (unit: any) => unit.unit_id === formik.values.unit_id
                    ) || null
                  }
                  onChange={(event, val) => {
                    formik.setFieldValue("unit_id", val?.unit_id || "");
                  }}
                  onBlur={() => formik.setFieldTouched("unit_id", true)}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        typeof opt === "string" ? opt : opt.unit_name
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      name="unit_id"
                      placeholder="Select Unit"
                      error={
                        formik.touched.unit_id && Boolean(formik.errors.unit_id)
                      }
                      helperText={
                        formik.touched.unit_id && formik.errors.unit_id
                      }
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.unit_id}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* GST */}
            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-3">
                <Form.Label>
                  GST % <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  disabled
                  // readOnly
                  type="text"
                  name="item_gst"
                  placeholder="Select HSN Code"
                  value={formik.values.item_gst}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.item_gst && formik.touched.item_gst
                  }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.item_gst}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* Selling Price */}
            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-3">
                <Form.Label>
                  Selling Price <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="selling_price"
                  placeholder="Enter Selling Price"
                  value={formik.values.selling_price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.selling_price &&
                    formik.touched.selling_price
                  }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.selling_price}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* Color Dropdown */}
            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-3">
                <Form.Label>
                  Color <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={colorFields}
                  getOptionLabel={(option: string) => option}
                  value={formik.values.color || null}
                  onChange={(event, val) => {
                    formik.setFieldValue("color", val || "");
                  }}
                  onBlur={() => formik.setFieldTouched("color", true)}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: string) => opt
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      name="color"
                      placeholder="Select Color"
                      error={
                        formik.touched.color && Boolean(formik.errors.color)
                      }
                      helperText={formik.touched.color && formik.errors.color}
                      fullWidth
                      size="small"
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>

            {/* Remark */}
            <Col lg={3} md={4} sm={6} xs={12}>
              <div className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  type="text"
                  name="remark"
                  placeholder="Enter Remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.remark && formik.touched.remark}
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.remark}
                </Form.Control.Feedback>
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton to="/item" />

            {/* Reusable submit button with spinner */}
            <SubmitButton isSubmitting={formik.isSubmitting} isEdit={isEdit} />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default ItemForm;
